import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import { HERO_BG, fmtNum } from "@/data/gameData";
import { Flame, Gamepad2, Dices, Sparkles, Trophy, ArrowRight, Loader2, Play, Crown } from "lucide-react";

const bigBtns = [
  { key: "daily", label: "DESAFIO DO DIA", sub: "A situação especial de hoje", icon: Flame, color: "#F59E0B", to: "/desafio" },
  { key: "story", label: "COMEÇAR HISTÓRIA", sub: "Escolha uma categoria", icon: Gamepad2, color: "#F43F5E", to: "/historias" },
  { key: "whatif", label: "E SE...?", sub: "Cenários inesperados", icon: Sparkles, color: "#FACC15", to: "/e-se" },
  { key: "surprise", label: "SURPREENDA-ME", sub: "História aleatória", icon: Dices, color: "#06B6D4", to: "/jogar/surpresa" },
];

export default function Home() {
  const { http } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([http.get("/me/summary"), http.get("/game/active")])
      .then(([s, a]) => { setSummary(s.data); setActive(a.data.active); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [http]);

  const last = summary?.last_result;
  const nextAchievements = (summary?.achievements || []).filter((a) => !a.unlocked).slice(0, 3);
  const streak = summary?.streak || 0;

  return (
    <div className="min-h-screen">
      <Header />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#05050A]/40 via-[#05050A]/70 to-[#05050A]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-5 pt-12 pb-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[#F43F5E] text-xs uppercase tracking-[0.3em] font-semibold">Simulador de decisões</p>
            {streak > 0 && (
              <div data-testid="home-streak" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/30 text-[#F59E0B] text-sm font-bold">
                <Flame className="w-4 h-4" /> {streak} {streak === 1 ? "dia" : "dias"}
              </div>
            )}
          </div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="font-display font-black text-5xl sm:text-6xl tracking-tighter leading-[0.95]">
            E SE FOSSE<br /><span className="text-[#F43F5E] text-glow">COMIGO?</span>
          </motion.h1>
          <p className="mt-4 text-lg text-slate-300 max-w-md">Uma escolha muda tudo. Cada decisão constrói a sua história.</p>

          {active && (
            <motion.button data-testid="home-continue-btn"
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              onClick={() => navigate(`/jogar/${active.category}?resume=${active.session_id}`)}
              className="mt-6 group flex items-center gap-3 rounded-2xl pl-4 pr-5 py-3 bg-[#F43F5E] hover:bg-[#E11D48] active:scale-95 transition-all shadow-lg shadow-[#F43F5E]/30">
              <span className="w-9 h-9 rounded-full bg-white/20 grid place-items-center"><Play className="w-4 h-4 fill-white" /></span>
              <span className="text-left">
                <span className="block text-[11px] uppercase tracking-widest opacity-80">Continuar história</span>
                <span className="block font-semibold leading-tight truncate max-w-[220px]">{active.titulo} · {active.step}/{active.total}</span>
              </span>
            </motion.button>
          )}
        </div>
      </section>

      {/* Actions */}
      <section className="max-w-5xl mx-auto px-5 -mt-1 pb-4">
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {bigBtns.map((b, i) => (
            <motion.button key={b.key} data-testid={`home-${b.key}-btn`}
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.06 }}
              onClick={() => navigate(b.to)}
              className="group relative text-left rounded-3xl p-5 bg-[#0F0F14] border border-white/10 overflow-hidden hover:-translate-y-1 transition-transform"
              style={{ boxShadow: `0 10px 40px -22px ${b.color}` }}>
              <div className="absolute -right-6 -top-6 w-20 h-20 rounded-full blur-2xl opacity-30 group-hover:opacity-60 transition-opacity" style={{ background: b.color }} />
              <div className="w-10 h-10 rounded-2xl grid place-items-center mb-6" style={{ background: `${b.color}22`, color: b.color }}>
                <b.icon className="w-5 h-5" />
              </div>
              <p className="font-display font-black text-base tracking-tight leading-tight">{b.label}</p>
              <p className="text-xs text-slate-400 mt-1">{b.sub}</p>
            </motion.button>
          ))}
        </div>
        <button data-testid="home-ranking-btn" onClick={() => navigate("/ranking")}
          className="mt-4 w-full group flex items-center justify-center gap-2 rounded-2xl py-4 bg-[#FACC15]/10 border border-[#FACC15]/30 text-[#FACC15] font-semibold hover:bg-[#FACC15]/20 transition-colors">
          <Trophy className="w-5 h-5" /> Ver Ranking Global
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </button>
      </section>

      {/* Retention dashboard */}
      <section className="max-w-5xl mx-auto px-5 py-8 grid gap-4 md:grid-cols-2">
        {loading ? (
          <div className="col-span-full flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#F43F5E]" /></div>
        ) : (
          <>
            <div data-testid="home-progress" className="rounded-3xl p-6 bg-[#0F0F14] border border-white/10">
              <p className="text-xs uppercase tracking-widest text-slate-500 mb-4">Seu progresso</p>
              <div className="grid grid-cols-3 gap-3 text-center">
                {[
                  { v: fmtNum(summary?.games || 0), l: "Histórias" },
                  { v: fmtNum(summary?.decisions_total || 0), l: "Decisões" },
                  { v: `${streak}🔥`, l: "Sequência" },
                ].map((s) => (
                  <div key={s.l}>
                    <p className="font-display font-black text-2xl">{s.v}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">{s.l}</p>
                  </div>
                ))}
              </div>
              <button onClick={() => navigate("/perfil")} className="inline-flex items-center gap-1 text-xs text-[#06B6D4] mt-5">
                Meu Jeito de Decidir <ArrowRight className="w-3 h-3" />
              </button>
            </div>

            <div data-testid="home-last-result" className="rounded-3xl p-6 bg-[#0F0F14] border border-white/10">
              <p className="text-xs uppercase tracking-widest text-slate-500 mb-3">Seu último resultado</p>
              {last ? (
                <button onClick={() => navigate(`/resultado/${last.session_id}`)} className="text-left w-full">
                  <p className="font-display font-black text-3xl text-[#F43F5E]">{fmtNum(last.score)}</p>
                  <p className="text-sm text-slate-300 mt-1 truncate">{last.persona?.emoji} {last.persona?.nome}</p>
                  <span className="inline-flex items-center gap-1 text-xs text-[#06B6D4] mt-3">Ver resultado <ArrowRight className="w-3 h-3" /></span>
                </button>
              ) : <p className="text-sm text-slate-500">Você ainda não jogou. Comece agora!</p>}
            </div>

            <div data-testid="home-next-achievements" className="rounded-3xl p-6 bg-[#0F0F14] border border-white/10 md:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs uppercase tracking-widest text-slate-500">Próximas conquistas</p>
                <button onClick={() => navigate("/conquistas")} className="text-xs text-[#06B6D4] flex items-center gap-1">
                  Ver todas <ArrowRight className="w-3 h-3" />
                </button>
              </div>
              {nextAchievements.length ? (
                <div className="grid gap-3 sm:grid-cols-3">
                  {nextAchievements.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10">
                      <span className="text-2xl grayscale opacity-50">{a.emoji}</span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{a.nome}</p>
                        <p className="text-[11px] text-slate-500 leading-tight line-clamp-2">{a.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-[#F59E0B]"><Crown className="w-5 h-5" /> Você desbloqueou tudo! 🎉</div>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
